import { orderBy } from 'lodash';
import { useCallback, useRef } from 'react';

import { type Clip } from '@air/api/types';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';

import { useAddAssetsToBoardsModal } from '~/components/Modals/AddAssetsToBoardsModal/useAddAssetsToBoardsModal';
import { DeletePrivateClipsModal } from '~/components/Modals/DeletePrivateClipsModal';
import { EditClipsTagsModal } from '~/components/Modals/EditClipsTagsModal';
import { MoveItemsModal } from '~/components/Modals/MoveItemsModal/MoveItemsModal';
import { useRemoveAssetsFromBoardModal } from '~/components/Modals/RemoveClipsFromBoardModal/hooks/useRemoveAssetsFromBoardModal';
import { SetBoardThumbnailModal } from '~/components/Modals/SetBoardThumbnail/SetBoardThumbnailModal';
import {
  canSharePrivateAsset,
  useSharePrivateAsset,
} from '~/components/SharePrivateClipModal/hooks/useSharePrivateAsset';
import { type OnRenameFunction } from '~/components/TableView/types';
import { PrivateContextSourcesAddClipModal } from '~/components/v2/components/context-sources/modals/PrivateContextSourcesAddClipModal';
import { useCanEditContextSources } from '~/components/v2/hooks/useCanEditContextSources';
import { type GetItemMenuOptionsCallback } from '~/constants/MenuOptions';
import { useCanCreateCanvasInCurrentLocation } from '~/hooks/canvases/useCanCreateCanvasInCurrentLocation';
import { useOpenClipInCanvas } from '~/hooks/canvases/useOpenClipInCanvas';
import { useIsFlattenedView } from '~/hooks/search/useIsFlattenedView';
import { useAddToShowcaseOption } from '~/hooks/useAddToShowcaseOption';
import { useAssetPermissionsCache } from '~/hooks/useAssetPermissionsCache';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useCanSetBoardThumbnail } from '~/hooks/useCanSetBoardThumbnail';
import { useCanShowInFinderOption } from '~/hooks/useCanShowInFinderOption';
import { useClipPermissionsCache } from '~/hooks/useClipPermissionsCache';
import { useDownloadPrivateAsset } from '~/hooks/useDownloadPrivateAsset';
import { useDownloadPrivateProxyAsset } from '~/hooks/useDownloadPrivateProxyAsset';
import { useGetPrivateCustomFieldsMenuOptions } from '~/hooks/useGetCustomFieldsMenuOptions';
import { useGetPrivateClipExportAsHandler } from '~/hooks/useGetPrivateClipExportAsHandler';
import { useGetPrivateClipMenuOptions } from '~/hooks/useGetPrivateClipMenuOptions';
import { useGoToPrivateAssetPage } from '~/hooks/useGoToPrivateAssetPage';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useDesktopAppConnectionContext } from '~/providers/DesktopAppConnectionProvider';
import { CentralizedBoardSelectors, centralizedBoardTitleSelector } from '~/store/centralizedBoard/selectors';
import { useToggleFavorite } from '~/swr-hooks/clips/useToggleFavorite';
import { getEditInCanvasOpenedFrom } from '~/utils/canvases/getEditInCanvasAnalytics';
import { getSetBoardThumbnailMenuSelect } from '~/utils/menuOptions/getSetBoardThumbnailMenuSelect';
import {
  canAddAssetToBoard,
  canChangeAssetCustomFields,
  canChangeVersionDetails,
  canDeleteAsset,
  canDownloadVersion,
  canEditClipTags,
  canMoveAsset,
  canRemoveClipTags,
} from '~/utils/permissions/assetPermissions';
import { canRemoveAssetsFromBoard } from '~/utils/permissions/boardPermissions';
import { useAirStore } from '~/utils/ReduxUtils';

const emptyArray: DropdownMenuOption[] = [];

type UseGetPrivateTableViewAssetMenuOptionsCallbackParams = {
  onRename?: OnRenameFunction;
};

export const useGetPrivateTableViewAssetMenuOptionsCallback = ({
  onRename,
}: UseGetPrivateTableViewAssetMenuOptionsCallbackParams = {}) => {
  const onRenameRef = useRef<OnRenameFunction | undefined>(undefined);
  onRenameRef.current = onRename;
  const store = useAirStore();
  const { goToAssetPage } = useGoToPrivateAssetPage();
  const { canShowOpenInFinder } = useCanShowInFinderOption();
  const { isFlattenedView } = useIsFlattenedView();

  const { openClipInFinder } = useDesktopAppConnectionContext();
  const { toggleFavorite } = useToggleFavorite();
  const { downloadPrivateAsset } = useDownloadPrivateAsset();

  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const { canCreateCanvas } = useCanCreateCanvasInCurrentLocation();
  const { canSetBoardThumbnail } = useCanSetBoardThumbnail();
  const { getClipPermissions } = useClipPermissionsCache();
  const { getBoardPermissions } = useBoardPermissionsCache();
  const { getAssetPermissions } = useAssetPermissionsCache();
  const { downloadPrivateProxyAsset } = useDownloadPrivateProxyAsset();
  const { canEditContextSources } = useCanEditContextSources();

  const { getCustomFieldsMenuOptions } = useGetPrivateCustomFieldsMenuOptions();
  const getAddToShowcaseOption = useAddToShowcaseOption();
  const { getClipMenuOptions } = useGetPrivateClipMenuOptions();
  const { openClipInCanvas } = useOpenClipInCanvas();
  const { getExportAsHandler } = useGetPrivateClipExportAsHandler({ canCreateCanvas, openClipInCanvas });

  const [showEditClipsTagsModal] = useAirModal(EditClipsTagsModal);
  const { showAddAssetsToBoardsModal } = useAddAssetsToBoardsModal();
  const { showRemoveAssetsFromBoardModal } = useRemoveAssetsFromBoardModal();
  const [showDeletePrivateClipsModal] = useAirModal(DeletePrivateClipsModal);
  const { openShareModal } = useSharePrivateAsset();
  const [showMoveToBoardModal] = useAirModal(MoveItemsModal);
  const [showSetBoardThumbnailModal] = useAirModal(SetBoardThumbnailModal);
  const [showAddContextSourceClipModal] = useAirModal(PrivateContextSourcesAddClipModal);

  const getTableViewAssetMenuOptionsCallback: GetItemMenuOptionsCallback<Clip> = useCallback(
    ({ item: clip }) =>
      ({ trackLocation }) => {
        const currentBoardId = CentralizedBoardSelectors.boardId(store.getState());
        const currentBoardTitle = centralizedBoardTitleSelector(store.getState());
        const currentBoardPermissions = currentBoardId ? getBoardPermissions(currentBoardId) : undefined;

        const clipPermissions = getClipPermissions(clip.id);
        const assetPermissions = getAssetPermissions(clip.assetId);

        const canDownloadClip = canDownloadVersion(clipPermissions);
        const canRemoveAssets = currentBoardId && canRemoveAssetsFromBoard(currentBoardPermissions);
        const canRenameClip = canChangeVersionDetails(clipPermissions);
        const canMove = canMoveAsset(assetPermissions);
        const canShare = canSharePrivateAsset(clipPermissions);
        const canEditTags = canEditClipTags(clipPermissions) && canRemoveClipTags(clipPermissions);
        const canDelete = canDeleteAsset(currentBoardId ? currentBoardPermissions : workspacePermissions);
        const canAddToBoard = canAddAssetToBoard(assetPermissions);
        const canManageApplications = canChangeAssetCustomFields(assetPermissions);

        if (!clipPermissions) {
          return emptyArray;
        }

        const editInCanvasOpenedFrom = getEditInCanvasOpenedFrom(trackLocation);

        return getClipMenuOptions({
          clips: [clip],
          customFieldsMenuOptions: canManageApplications ? getCustomFieldsMenuOptions({ assets: [clip] }) : undefined,
          addToShowcaseOption: getAddToShowcaseOption
            ? getAddToShowcaseOption({ assetIds: [clip.assetId], boardIds: [] })
            : undefined,

          onAddToBoard: canAddToBoard
            ? () =>
                showAddAssetsToBoardsModal({
                  clipIds: [clip.id],
                  trackLocation,
                  parentBoardId: currentBoardId,
                })
            : undefined,

          editInCanvas: canCreateCanvas
            ? {
                // The selection bar reuses this menu for a single selected asset.
                canOfferSizeSet: trackLocation !== 'action-bar',
                onSelect: (selection) =>
                  openClipInCanvas({ ...selection, clips: [clip], openedFrom: editInCanvasOpenedFrom }),
                openedFrom: editInCanvasOpenedFrom,
              }
            : undefined,

          onAddToContext: canEditContextSources
            ? () => {
                showAddContextSourceClipModal({
                  clip,
                });
              }
            : undefined,

          onEditTags: canEditTags
            ? () => {
                showEditClipsTagsModal({
                  clipIds: [clip.id],
                  trackLocation,
                  initialTags: clip.tags ?? [],
                });
              }
            : undefined,
          onDelete: canDelete
            ? () =>
                showDeletePrivateClipsModal({
                  assetIds: [clip.assetId],
                  trackLocation,
                  parentBoardId: currentBoardId,
                })
            : undefined,
          onDownload: canDownloadClip ? () => downloadPrivateAsset({ clipId: clip.id, trackLocation }) : undefined,
          onExportAs: getExportAsHandler({ canDownload: canDownloadClip, clip, openedFrom: 'table_item_export_as' }),
          originalResolution: canDownloadClip
            ? {
                id: 'original',
                label: `${clip.ext.toUpperCase()} - ${clip.width}x${clip.height}`,
                onSelect: () => downloadPrivateAsset({ clipId: clip.id, trackLocation }),
                type: 'item',
              }
            : undefined,
          altResolutions: canDownloadClip
            ? orderBy(clip.altResolutions, (resolution) => resolution.width * resolution.height, 'desc').map(
                (resolution) => ({
                  label: `${resolution.ext} - ${resolution.width}x${resolution.height}`,
                  id: `proxy-${resolution.width}-${resolution.height}`,
                  onSelect: () =>
                    downloadPrivateProxyAsset({ id: clip.id, resolutionId: resolution.id, trackLocation }),
                  type: 'item',
                }),
              )
            : undefined,
          onMoveToBoard:
            currentBoardId && canMove
              ? () =>
                  showMoveToBoardModal({
                    boards: [],
                    clips: [
                      {
                        id: clip.id,
                        assetId: clip.assetId,
                        openCommentCount: clip.openCommentCount,
                      },
                    ],
                    trackLocation,
                  })
              : undefined,
          onOpen: () =>
            goToAssetPage({
              asset: clip,
              trackLocation: 'open-dropdown-select',
            }),
          onOpenOnDesktop:
            /**
             * @see https://air-labs-team.slack.com/archives/C052RDYCGAG/p1696874696775379
             */
            canShowOpenInFinder && canDownloadClip
              ? () => {
                  openClipInFinder(clip, currentBoardId);
                }
              : undefined,
          onRemoveFromCurrentBoard:
            !isFlattenedView && currentBoardId && currentBoardTitle && canRemoveAssets
              ? () => {
                  showRemoveAssetsFromBoardModal({
                    boardId: currentBoardId,
                    boardTitle: currentBoardTitle,
                    clipIds: [clip.id],
                    trackLocation,
                  });
                }
              : undefined,
          onRenameAssetInline: canRenameClip ? () => onRenameRef.current?.() : undefined,
          onSetBoardThumbnail: getSetBoardThumbnailMenuSelect({
            canSetThumbnail: canSetBoardThumbnail,
            clip,
            excludeRive: true,
            showSetBoardThumbnailModal,
          }),
          onShare: canShare ? () => openShareModal(clip, trackLocation) : undefined,

          onFavorite: !clip.bookmarked ? () => toggleFavorite({ clips: [clip], trackLocation }) : undefined,
          onUnfavorite: clip.bookmarked ? () => toggleFavorite({ clips: [clip], trackLocation }) : undefined,
        });
      },
    [
      store,
      getBoardPermissions,
      getClipPermissions,
      getAssetPermissions,
      workspacePermissions,
      canCreateCanvas,
      canSetBoardThumbnail,
      getClipMenuOptions,
      getExportAsHandler,
      getCustomFieldsMenuOptions,
      canShowOpenInFinder,
      isFlattenedView,
      showAddAssetsToBoardsModal,
      showEditClipsTagsModal,
      showDeletePrivateClipsModal,
      downloadPrivateAsset,
      downloadPrivateProxyAsset,
      showMoveToBoardModal,
      goToAssetPage,
      openClipInFinder,
      showRemoveAssetsFromBoardModal,
      showSetBoardThumbnailModal,
      openShareModal,
      toggleFavorite,
      getAddToShowcaseOption,
      canEditContextSources,
      showAddContextSourceClipModal,
      openClipInCanvas,
    ],
  );

  return {
    getTableViewAssetMenuOptionsCallback,
  };
};
